import React, { useState } from 'react';
import { BaseChat, BaseChatConfig } from '@/components/chat/BaseChat';
import { sendExecutionDiagnosisChatMessage } from '@/api/websocket';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/api/client';

interface ExecutionDiagnosisChatProps {
  executionId: string;
  workflowId: string;
  workflowName: string;
  onClose: () => void;
}

export function ExecutionDiagnosisChat({ executionId, workflowId, workflowName, onClose }: ExecutionDiagnosisChatProps) {
  const navigate = useNavigate();
  const [deletedModal, setDeletedModal] = useState<{ workflow: any } | null>(null);
  const [creating, setCreating] = useState(false);

  const handleEditWorkflow = async (workflow: any) => {
    try {
      await apiClient.getWorkflow(workflowId);
      navigate(`/workflows/${workflowId}/edit`, {
        state: { suggestedDefinition: workflow.definition || workflow }
      });
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setDeletedModal({ workflow });
      } else {
        navigate(`/workflows/${workflowId}/edit`, {
          state: { suggestedDefinition: workflow.definition || workflow }
        });
      }
    }
  };

  const handleCreateFromSuggestion = async () => {
    if (!deletedModal) return;
    setCreating(true);
    try {
      const wf = deletedModal.workflow;
      const created = await apiClient.createWorkflow({
        name: wf.name || workflowName || 'Fixed Workflow',
        description: wf.description || '',
        definition: wf.definition || { nodes: [], edges: [] },
        trigger_config: wf.trigger_config || { source: 'kubernetes', signals: [], cooldown_hours: 0, max_concurrent_executions: 3 },
      });
      setDeletedModal(null);
      navigate(`/workflows/${created.id}/edit`);
    } catch {
      setCreating(false);
    }
  };

  const config: BaseChatConfig = {
    sendMessage: (inputText: string) =>
      sendExecutionDiagnosisChatMessage(inputText, executionId, workflowId),
    samplePrompts: [
      'Why did this workflow execution fail?',
      'What do I need to fix to make this workflow succeed?',
      'Is this a permissions issue or a configuration issue?',
      'Show me the exact error and suggest a fix',
    ],
    headerIcon: undefined,
    headerTitle: 'Failure Diagnosis',
    emptyHeading: 'Diagnose this execution failure',
    emptyDescription: 'Ask questions about why this workflow execution failed and how to fix it.',
    inputPlaceholder: 'Ask about this failure...',
    colorTheme: 'primary',
    fixType: 'yaml',
    contextType: 'execution_diagnosis',
    contextId: executionId,
    hideEmptyIcon: true,
    hideDisclaimer: true,
    requiredProduct: 'workflows',
    onWorkflowEdit: handleEditWorkflow,
    renderHeaderExtra: () => (
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} title="Close">
        <X className="h-4 w-4" />
      </Button>
    ),
  };

  return (
    <>
      <BaseChat config={config} />
      {deletedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDeletedModal(null)}>
          <div className="bg-background border rounded-lg shadow-xl max-w-md w-full m-4" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b">
              <h3 className="text-base font-semibold">Workflow Deleted</h3>
            </div>
            <div className="px-5 py-4">
              <p className="text-sm text-muted-foreground">
                The original workflow <span className="font-medium text-foreground">{workflowName}</span> has been deleted. Would you like to create a new workflow from the suggested fix?
              </p>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t">
              <Button variant="outline" size="sm" onClick={() => setDeletedModal(null)}>Cancel</Button>
              <Button size="sm" onClick={handleCreateFromSuggestion} disabled={creating}>
                {creating ? 'Creating...' : 'Create Workflow'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
